import type {
    EngagementVoteResponse,
    VoteType,
} from "@mrflark/spl_sdk";
import {
    useMutation,
} from "@tanstack/react-query";
import {
    useCallback,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import {
    splClient,
} from "../api/client.ts";
import {
    useViewerStore,
} from "../stores/viewerStore.ts";

type UseViewerMutationsReturn = {
    handleToggleTag: (tag: string) => void;
    handleAddTag: (tag: string) => void;
    handleRemoveTag: (tag: string) => void;
    handleVote: (voteType: VoteType) => void;
    voteOnMediaMutation: ReturnType<
        typeof useMutation<EngagementVoteResponse, Error, VoteType>
    >;
    removeVoteMutation: ReturnType<typeof useMutation<void, Error, void>>;
    votePending: boolean;
    tagMutation: ReturnType<
        typeof useMutation<{ tags: string[]; }, Error, string[]>
    >;
    deleteMutation: ReturnType<typeof useMutation<unknown, Error, void>>;
    acceptSuggestionMutation: ReturnType<
        typeof useMutation<
            { tags: string[]; remaining_suggestions: string[]; },
            Error,
            string
        >
    >;
    rejectSuggestionMutation: ReturnType<
        typeof useMutation<{ remaining_suggestions: string[]; }, Error, string>
    >;
};

export const useViewerMutations = (
    onDeleteSuccess: () => void,
): UseViewerMutationsReturn => {
    const {
        currentItem,
        currentUserVote,
        updateCurrentItemTags,
        updateCurrentItemVoteCounts,
        setCurrentUserVote,
    } = useViewerStore();

    const voteOnMediaMutation = useMutation({
        mutationFn: (voteType: VoteType) => {
            if (!currentItem) throw new Error("No item selected");
            return splClient.voteOnMedia(currentItem.id, voteType);
        },
        onSuccess: (data) => {
            setCurrentUserVote(data.vote);
            updateCurrentItemVoteCounts(
                data.likeCount,
                data.dislikeCount,
                data.loveCount,
            );
        },
        onError: () => {
            toast.error("Failed to vote");
        },
    });

    const removeVoteMutation = useMutation({
        mutationFn: () => {
            if (!currentItem) throw new Error("No item selected");
            return splClient.removeVote(currentItem.id);
        },
        onSuccess: () => {
            setCurrentUserVote(null);
        },
        onError: () => {
            toast.error("Failed to remove vote");
        },
    });

    const handleVote = useCallback((voteType: VoteType) => {
        if (!currentItem) return;
        if (currentUserVote === voteType) {
            removeVoteMutation.mutate();
        } else {
            voteOnMediaMutation.mutate(voteType);
        }
    }, [currentItem, currentUserVote, voteOnMediaMutation, removeVoteMutation]);

    const votePending = voteOnMediaMutation.isPending ||
        removeVoteMutation.isPending;

    const tagMutation = useMutation({
        mutationFn: (tags: string[]) => {
            if (!currentItem) throw new Error("No item selected");
            return splClient.updateTags(currentItem.id, tags);
        },
        onSuccess: (data) => {
            updateCurrentItemTags(data.tags);
        },
        onError: () => {
            toast.error("Failed to update tags");
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!currentItem) throw new Error("No item selected");
            return splClient.bulkAction({
                action: "delete",
                ids: [currentItem.id],
            });
        },
        onSuccess: () => {
            toast.success("Item deleted");
            onDeleteSuccess();
        },
        onError: () => {
            toast.error("Failed to delete item");
        },
    });

    const acceptSuggestionMutation = useMutation({
        mutationFn: (tag: string) => {
            if (!currentItem) throw new Error("No item selected");
            return splClient.acceptSuggestion(currentItem.id, tag);
        },
        onSuccess: (data) => {
            updateCurrentItemTags(data.tags);
            toast.success("Tag accepted");
        },
        onError: () => {
            toast.error("Failed to accept suggestion");
        },
    });

    const rejectSuggestionMutation = useMutation({
        mutationFn: (tag: string) => {
            if (!currentItem) throw new Error("No item selected");
            return splClient.rejectSuggestion(currentItem.id, tag);
        },
        onSuccess: () => {
            toast.success("Suggestion dismissed");
        },
        onError: () => {
            toast.error("Failed to dismiss suggestion");
        },
    });

    const handleToggleTag = useCallback((tag: string) => {
        if (!currentItem) return;
        const currentTags = currentItem.tags || [];
        const newTags = currentTags.includes(tag)
            ? currentTags.filter((existing) => existing !== tag)
            : [...currentTags, tag];
        tagMutation.mutate(newTags);
    }, [currentItem, tagMutation]);

    const handleAddTag = useCallback((tag: string) => {
        if (!currentItem) return;
        const currentTags = currentItem.tags || [];
        if (!currentTags.includes(tag)) {
            tagMutation.mutate([...currentTags, tag]);
        }
    }, [currentItem, tagMutation]);

    const handleRemoveTag = useCallback((tag: string) => {
        if (!currentItem) return;
        const currentTags = currentItem.tags || [];
        tagMutation.mutate(currentTags.filter((existing) => existing !== tag));
    }, [currentItem, tagMutation]);

    return {
        handleToggleTag,
        handleAddTag,
        handleRemoveTag,
        handleVote,
        voteOnMediaMutation,
        removeVoteMutation,
        votePending,
        tagMutation,
        deleteMutation,
        acceptSuggestionMutation,
        rejectSuggestionMutation,
    };
};
